import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useStages } from '../redux/hooks/useStages';
import { useCandidates } from '../redux/hooks/useCandidates';
import StageSummary from '../components/dashboard/StageSummary';
import {
    getRecentActivities,
    getUpcomingInterviews
} from '../services/api/dashboardService';
import { DashboardStats, RecentActivity, UpcomingInterview } from '../types/types';
import LinkButton from '../components/common/LinkButton';

const DashboardPage: React.FC = () => {
    // Redux hooks
    const {
        stageStats,
        totalCandidateCount,
        newCandidateCount,
        loading: stagesLoading,
        error: stagesError,
        fetchWithCounts: fetchStagesWithCounts
    } = useStages();

    const {
        candidateStats,
        loading: candidatesLoading,
        error: candidatesError,
        ensureCandidatesLoaded,
        ensureRecentCandidatesLoaded
    } = useCandidates();

    // Local state - same as original
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isLoadingActivities, setIsLoadingActivities] = useState(true);
    const [isLoadingInterviews, setIsLoadingInterviews] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [stats, setStats] = useState<DashboardStats>({
        totalCandidates: 0,
        newCandidatesThisWeek: 0,
        totalJobs: 0,
        activeJobs: 0,
        stageStats: [],
    });

    const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
    const [upcomingInterviews, setUpcomingInterviews] = useState<UpcomingInterview[]>([]);

    useEffect(() => {
        const fetchDashboardStats = async () => {
            setIsLoadingStats(true);
            setError(null);

            try {
                // Use Redux data instead of API call
                await fetchStagesWithCounts();
                await ensureCandidatesLoaded();
                await ensureRecentCandidatesLoaded();
            } catch (err) {
                console.error('Error fetching dashboard stats:', err);
                setError('Failed to load dashboard statistics');
            } finally {
                setIsLoadingStats(false);
            }
        };

        const fetchRecentActivities = async () => {
            setIsLoadingActivities(true);

            try {
                const data = await getRecentActivities(5); // Get 5 most recent activities
                setRecentActivities(data);
            } catch (err) {
                console.error('Error fetching recent activities:', err);
                // Don't set error - we want to show other data even if this fails
            } finally {
                setIsLoadingActivities(false);
            }
        };

        const fetchUpcomingInterviews = async () => {
            setIsLoadingInterviews(true);

            try {
                const data = await getUpcomingInterviews(7); // Get interviews for next 7 days
                setUpcomingInterviews(data);
            } catch (err) {
                console.error('Error fetching upcoming interviews:', err);
                // Don't set error - we want to show other data even if this fails
            } finally {
                setIsLoadingInterviews(false);
            }
        };

        // Fetch all data in parallel
        fetchDashboardStats();
        fetchRecentActivities();
        fetchUpcomingInterviews();
    }, [fetchStagesWithCounts, ensureCandidatesLoaded, ensureRecentCandidatesLoaded]);

    // Separate useEffect to update stats when Redux data changes
    useEffect(() => {
        if (!isLoadingStats && totalCandidateCount !== undefined && stageStats.length > 0) {
            setStats(prevStats => {
                const newStats = {
                    totalCandidates: totalCandidateCount,
                    newCandidatesThisWeek: newCandidateCount,
                    totalJobs: candidateStats.total > 0 ? Object.keys(candidateStats.byJob).length : 0,
                    activeJobs: candidateStats.total > 0 ? Object.keys(candidateStats.byJob).length : 0,
                    stageStats: stageStats.map(stage => ({
                        stageId: stage.id,
                        stageName: stage.name,
                        count: stage.count,
                        displayOrder: stage.displayOrder
                    })),
                };

                // Only update if data actually changed
                if (JSON.stringify(prevStats) !== JSON.stringify(newStats)) {
                    return newStats;
                }
                return prevStats;
            });
        }
    }, [totalCandidateCount, newCandidateCount, candidateStats, stageStats, isLoadingStats]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Dashboard</h1>
                <div>
                    <LinkButton
                        to="/candidates/new"
                        variant="primary"
                        className="me-2"
                    >
                        <i className="bi bi-person-plus me-1"></i> Add Candidate
                    </LinkButton>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            <Row className="mb-4">
                <Col sm={6} lg={3}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body>
                            {isLoadingStats ? (
                                <div className="text-center p-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Candidates</h6>
                                        <h3 className="mb-0">{stats.totalCandidates}</h3>
                                    </div>
                                    <div className="bg-light rounded-circle p-3">
                                        <i className="bi bi-people fs-4 text-primary"></i>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} lg={3}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body>
                            {isLoadingStats ? (
                                <div className="text-center p-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">New This Week</h6>
                                        <h3 className="mb-0">{stats.newCandidatesThisWeek}</h3>
                                    </div>
                                    <div className="bg-light rounded-circle p-3">
                                        <i className="bi bi-person-plus fs-4 text-success"></i>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} lg={3}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body>
                            {isLoadingStats ? (
                                <div className="text-center p-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Jobs</h6>
                                        <h3 className="mb-0">{stats.totalJobs}</h3>
                                    </div>
                                    <div className="bg-light rounded-circle p-3">
                                        <i className="bi bi-briefcase fs-4 text-warning"></i>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} lg={3}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body>
                            {isLoadingStats ? (
                                <div className="text-center p-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Active Jobs</h6>
                                        <h3 className="mb-0">{stats.activeJobs}</h3>
                                    </div>
                                    <div className="bg-light rounded-circle p-3">
                                        <i className="bi bi-briefcase-fill fs-4 text-info"></i>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-transparent">
                    <h5 className="mb-0">Candidates by Stage</h5>
                </Card.Header>
                <Card.Body>
                    <StageSummary
                        stages={stats.stageStats}
                        isLoading={isLoadingStats}
                    />
                </Card.Body>
            </Card>

            <Row className="mb-4">
                <Col md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Recent Activity</h5>
                            <LinkButton
                                variant="link"
                                size="sm"
                                className="p-0"
                                to="/activities"
                            >
                                View All
                            </LinkButton>
                        </Card.Header>
                        <Card.Body>
                            {isLoadingActivities ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : recentActivities.length === 0 ? (
                                <p className="text-muted mb-0">No recent activity to display</p>
                            ) : (
                                <div className="activity-list">
                                    {recentActivities.map((activity) => (
                                        <div
                                            key={`${activity.activityType}-${activity.timestamp}`}
                                            className="d-flex align-items-start mb-3"
                                        >
                                            <div
                                                className={`activity-icon rounded-circle d-flex align-items-center 
                                                    justify-content-center me-3 bg-light text-${getActivityColor(activity.activityType)}`}
                                                style={{ width: '36px', height: '36px' }}
                                            >
                                                <i className={`bi ${getActivityIcon(activity.activityType)}`}></i>
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-bold">
                                                    <Link to={`/candidates/${activity.candidateId}`} state={{ from: '/dashboard' }}>
                                                        {activity.candidateName}
                                                    </Link>
                                                </p>
                                                <p className="mb-0 small">
                                                    {activity.description}
                                                </p>
                                                <p className="text-muted small mb-0">
                                                    {formatDate(activity.timestamp.toString())} by {activity.userName}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Upcoming Interviews</h5>
                            <LinkButton
                                variant="link"
                                size="sm"
                                className="p-0"
                                to="/interviews"
                            >
                                View All
                            </LinkButton>
                        </Card.Header>
                        <Card.Body>
                            {isLoadingInterviews ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : upcomingInterviews.length === 0 ? (
                                <p className="text-muted mb-0">No upcoming interviews scheduled</p>
                            ) : (
                                <div className="interviews-list">
                                    {upcomingInterviews.map((interview) => (
                                        <div
                                            key={`${interview.candidateId}-${interview.interviewDate}`}
                                            className="d-flex align-items-start mb-3"
                                        >
                                            <div
                                                className={`interview-icon rounded-circle d-flex align-items-center 
                                                    justify-content-center me-3 ${interview.isToday ? 'bg-warning text-dark' : 'bg-light text-primary'}`}
                                                style={{ width: '36px', height: '36px' }}
                                            >
                                                <i className="bi bi-calendar-event"></i>
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-bold">
                                                    <Link to={`/candidates/${interview.candidateId}`} state={{ from: '/dashboard' }}>
                                                        {interview.candidateName}
                                                    </Link>
                                                </p>
                                                <p className="mb-0 small">
                                                    {interview.jobTitle || 'No job title'}
                                                </p>
                                                <p className="text-muted small mb-0">
                                                    {formatDate(interview.interviewDate.toString())}
                                                    {interview.isToday && (
                                                        <span className="badge bg-warning text-dark ms-2">Today</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

// Helper function to get the appropriate icon for an activity type
const getActivityIcon = (activityType: string): string => {
    switch (activityType.toLowerCase()) {
        case 'stagechange':
            return 'bi-arrow-right-circle-fill';
        case 'note':
            return 'bi-journal-text';
        case 'documentupload':
            return 'bi-file-earmark';
        case 'email':
            return 'bi-envelope';
        case 'interview':
            return 'bi-calendar-event';
        default:
            return 'bi-clock-history';
    }
};

// Helper function to get the appropriate color for an activity type
const getActivityColor = (activityType: string): string => {
    switch (activityType.toLowerCase()) {
        case 'stagechange':
            return 'success';
        case 'note':
            return 'info';
        case 'documentupload':
            return 'primary';
        case 'email':
            return 'warning';
        case 'interview':
            return 'danger';
        default:
            return 'secondary';
    }
};

export default DashboardPage;